import React, { useEffect, useState } from 'react';
import { Accordion, Button } from '@arcflight/tf-component-library';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Trip } from '../../models/trips';
import { Operator } from '../../models/userSettings';
import { DashboardState } from '../../models';
import { DisplayText, Header } from '../FlightDrawer/FlightDrawer';
import plusIcon from '../../assets/plus-blue.svg';
import AdditionalCrewIcon from '../../assets/icon-card-additional-crew.svg';
import { ErrorState } from '../TFInput/TFInput';
import { DataValue } from '../../utils/updateLocalDataObject';
import { queryPeople } from '../../services/api';
import EditAdditionalCrewSection from './EditAdditionalCrewSection';

const MAX_ADDITIONAL_CREW = 10;

interface AdditionalCrewSectionProps {
  trip: Trip;
  updateTripData: (key: string, value: DataValue, secondKey?: string, secondValue?: DataValue) => void;
  currentOp: Operator;
  formError: Map<string, { person: ErrorState }>;
  setFormError: (error: Map<string, { person: ErrorState }>) => void;
}

const CrewWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  border-radius: 2px;
  box-shadow: 0 0 10px 0 rgba(219, 227, 237, 0.41);
  border: solid 2px #fff;
  background-color: rgba(255, 255, 255, 0);
  margin: 0 0 32px;
  padding: 8px 20px;
  margin-bottom: ${({ noMargin }): string => (noMargin ? '0' : '20px')};
  grid-template-rows: auto;
`;

const SectionWrapper = styled.div`
  margin-bottom: 32px;
`;

export const SectionHeader = styled.div`
  color: #242d41;
  font-weight: 500;
  margin-bottom: 20px;
  margin-left: 20px;
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const AddButtonWrapper = styled.div`
  margin-right: 20px;
`;

const InputSection = styled.div`
  padding-top: 1.5rem;
  display: ${({ edit }): string => (edit ? 'block' : 'grid')};
  grid-template-columns: ${({ edit }): string => (edit ? '' : '1fr 1fr 1fr 1fr')};
`;

const AddTextWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const PlusIcon = styled.img`
  width: 9px;
  height: 9px;
  margin-right: 4px;
`;

const NoAdditionalCrewText = styled.span`
  margin-left: 8px;
`;

const UserWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AdditionalCrewSection: React.FC<AdditionalCrewSectionProps> = ({
  trip,
  updateTripData,
  currentOp,
  formError,
  setFormError,
}) => {
  const {
    drawer: { mode },
    people: { peopleMap, deletedPeopleMap, lastFetched, ttl },
  } = useSelector((state: DashboardState) => state);

  const { formatMessage } = useIntl();

  const [additionalCrew, setAdditionalCrew] = useState([]);
  const [personRoles, setPersonRoles] = useState([]);
  const [usersForOperator, setusersForOperator] = useState([]);
  const [combinedPeopleArray, setCombinedPeopleArray] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userOptions, setUserOptions] =
    useState<Record<string, Array<{ title: string; value: string | undefined }>>>({});

  const isMobile = window.innerWidth < 451;

  const isAtMaxAdditionalCrew = (): boolean => {
    // eslint-disable-next-line no-underscore-dangle
    const additionalCrewNotDestroyed = additionalCrew.filter(crew => !crew._destroy);
    return additionalCrewNotDestroyed.length >= MAX_ADDITIONAL_CREW;
  };

  const handleAddAdditionalCrew = (): void => {
    if (isAtMaxAdditionalCrew()) return;

    const crew = additionalCrew;
    const newCrew = {
      operator_person_role_id: null,
      person_id: null,
    };
    crew.push(newCrew);
    setAdditionalCrew([...crew]);
  };

  const removeCrew = (index): void => {
    const newArray = additionalCrew;
    const dutyLogs = trip.duty_logs || [];
    const dutyLogIndex = dutyLogs?.findIndex((dl) => dl?.person_id === newArray[index]?.person_id);
    const isStoredInCore = typeof newArray[index].id === 'string';
    if (isStoredInCore) {
      // eslint-disable-next-line no-underscore-dangle
      newArray[index]._destroy = true;
      if (dutyLogIndex !== -1) {
        // eslint-disable-next-line no-underscore-dangle
        dutyLogs[dutyLogIndex]._destroy = true;
      }
    } else {
      newArray.splice(index, 1);
      if (dutyLogIndex !== -1) {
        dutyLogs.splice(dutyLogIndex, 1);
      }
    }
    updateTripData('additional_crews_attributes', [...newArray], 'duty_logs_attributes', [...dutyLogs]);
  };

  const checkCurrentUserIsPartOfRoleGroup = (
    roleId: string,
    personId: string | null
  ): boolean => {
    if (!personId) return false;
    const userRole = usersForOperator.find(user => user.id === personId)?.position;
    const rolePositions = roles.find(role => role.value === roleId)?.positions;
    return Boolean(userRole && rolePositions?.includes(userRole));
  };

  const handleSelectedRole = (roleId: string, index: number): void => {
    const currentCrew = additionalCrew[index];
    const availableUsers = userOptions[roleId] || [];
    const isPersonInRole = currentCrew.person_id && (
      checkCurrentUserIsPartOfRoleGroup(roleId, currentCrew.person_id) ||
      availableUsers.some(user => user.value === currentCrew.person_id)
    );

    if (currentCrew.person_id && !isPersonInRole) {
      formError.set(
        index.toString(),
        { person: { status: 'error', message: formatMessage({ id: 'text.crewMemberNotAvailableForRole' }) } }
      );
    } else {
      formError.delete(index.toString());
    }

    setFormError(formError);
    const updatedCrew = additionalCrew.map((crew, i) =>
      i === index ? { ...crew, operator_person_role_id: roleId } : crew
    );
    setAdditionalCrew(updatedCrew);
    updateTripData('additional_crews_attributes', updatedCrew);
  };

  const handleSelectedPerson = (id: string, index: number): void => {
    const newArray = additionalCrew;
    newArray[index].person_id = id;
    formError.delete(index.toString());
    setFormError(formError);
    setAdditionalCrew([...newArray]);
    updateTripData('additional_crews_attributes', [...newArray]);
  };

  useEffect(() => {
    if (currentOp) {
      const captainId = trip?.captain_id || trip?.captain?.id;
      const firstOfficerId = trip?.first_officer_id || trip?.first_officer?.id;
      const currentAdditionalCrewIds =
        [...(trip?.additional_crews || []), ...(trip?.additional_crews_attributes || [])].map((c) => c.person_id);

      setPersonRoles(currentOp.person_roles);
      setRoles(currentOp.person_roles.map((r) => ({ title: r.name, value: r.id, positions: r.positions })));
      if (combinedPeopleArray.length) {
        const filteredUsersByOperator = combinedPeopleArray.filter(
          (p) => p.first_name !== null && p?.organisation?.id === currentOp?.id,
        ).sort((a, b) => a.first_name.localeCompare(b.first_name));
        setusersForOperator(filteredUsersByOperator);
        const filteredUsers = filteredUsersByOperator.filter((person) => {
          const idsNotToBeUsed = [...currentAdditionalCrewIds, captainId, firstOfficerId].filter((id) => id !== null);
          return !idsNotToBeUsed.includes(person.id);
        });

        const roleBasedUsers = currentOp.person_roles.reduce((acc, role) => {
          const filteredUsersForRole = filteredUsers.filter(user =>
            role.positions.includes(user.position)
          );
          const usersForRole = filteredUsersForRole.map((user) => ({
            title: `${user.first_name} ${user.last_name}`,
            value: user.id,
          }));
          usersForRole.push({ title: 'None', value: undefined });

          return {
            ...acc,
            [role.id]: usersForRole
          };
        }, {} as Record<string, Array<{ title: string; value: string | undefined }>>);

        setUserOptions(roleBasedUsers);
      }
    }
  }, [
    combinedPeopleArray,
    currentOp,
    trip?.additional_crews,
    trip?.additional_crews_attributes,
    trip?.captain?.id,
    trip?.captain_id,
    trip?.first_officer?.id,
    trip?.first_officer_id
  ]);

  useEffect(() => {
    if (peopleMap.size > 0) {
      setCombinedPeopleArray([...peopleMap.values(), ...deletedPeopleMap.values()]);
    }
  }, [deletedPeopleMap, peopleMap]);

  useEffect(() => {
    if ((peopleMap.size === 0 && deletedPeopleMap.size === 0) || lastFetched < Date.now() - ttl) {
      queryPeople({ with_deleted: true });
    }
  }, [deletedPeopleMap.size, lastFetched, peopleMap.size, ttl]);

  useEffect(() => {
    if (trip !== null && currentOp) {
      const additCrews = trip?.additional_crews_attributes || trip?.additional_crews;
      if (additCrews) {
        if (additCrews.length > 0) {
          if (additCrews[0].operator_person_role_id === undefined) {
            additCrews.forEach((crew) => {
              const currentCrew = currentOp.person_roles.find((pr) => pr.name === crew.position);
              if (currentCrew) {
                // eslint-disable-next-line no-param-reassign
                crew.operator_person_role_id = currentCrew.id;
              }
            });
          }
        }
        setAdditionalCrew(additCrews);
      }
    }
  }, [trip, currentOp]);

  let additionalCrewTitle = 'Additional crew added to trip';
  if (!additionalCrew?.length) additionalCrewTitle = 'No additional crew currently added';
  if (isMobile) {
    if (!additionalCrew?.length) additionalCrewTitle = 'No Additional crew';
    additionalCrewTitle = 'Additional crew added';
  }

  return (
    <SectionWrapper>
      <SectionHeader marginLeft="20px" data-testid="TripTrendingSection-Header">
        Additional Crew
      </SectionHeader>
      <Accordion
        headerContent={
          <HeaderInner>
            <div>
              <img src={AdditionalCrewIcon} alt="no defects" />
              <NoAdditionalCrewText>{additionalCrewTitle}</NoAdditionalCrewText>
            </div>
            {mode !== 'view' ? (
              <AddButtonWrapper>
                <Button
                  height="24px"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddAdditionalCrew();
                  }}
                  primary={false}
                  disabled={isAtMaxAdditionalCrew()}>
                  <AddTextWrapper>
                    <PlusIcon src={plusIcon} alt="plus icon" />
                    {isMobile ? 'Add' : 'Add crew'}
                  </AddTextWrapper>
                </Button>
              </AddButtonWrapper>
            ) : null}
          </HeaderInner>
        }
        dash
        isExpanded={mode === 'add'}
      >
        {additionalCrew.length === 0 ? null : (
          <CrewWrapper noMargin>
            {additionalCrew.length > 0 && (
              <InputSection edit={mode !== 'view'}>
                {additionalCrew.map((crew, index) => {
                  // eslint-disable-next-line no-underscore-dangle
                  if (crew._destroy) {
                    return null;
                  }
                  const user = usersForOperator.find((p) => p.id === crew.person_id);
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`${crew.person_id}-${index}`}>
                        {mode === 'view' ? (
                          <UserWrapper>
                            <Header editable={mode !== 'view'}>
                              {crew.position && personRoles.length
                                ? `${personRoles.find((p) => p.name === crew.position)?.name}`
                                : '-'}
                            </Header>
                            <DisplayText>
                              {crew.person_id && user ? `${user.first_name} ${user.last_name}` : '-'}
                            </DisplayText>
                          </UserWrapper>
                        ) : (
                          <EditAdditionalCrewSection
                            roles={roles}
                            users={userOptions[roles?.find((r) =>
                              r.value === additionalCrew[index]?.operator_person_role_id)?.value || '']}
                            foundRole={roles?.find((r) => r.value === additionalCrew[index]?.operator_person_role_id)}
                            foundPerson={user ?
                              { title: `${user?.first_name} ${user?.last_name}`, value: user?.id }
                              : null}
                            handleSelectedRole={handleSelectedRole}
                            handleSelectedPerson={handleSelectedPerson}
                            index={index}
                            removeCrew={removeCrew}
                            additionalCrewLength={additionalCrew.length}
                            formError={formError.get(index.toString())}
                          />
                        )}
                    </div>
                  );
                })}
              </InputSection>
            )}
          </CrewWrapper>
        )}
      </Accordion>
    </SectionWrapper>
  );
};

export default AdditionalCrewSection;
